import { ClickAwayListener, Box, Popper, alpha, useTheme, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { darken } from '@mui/system/colorManipulator';
import { EditableInput } from 'react-color/lib/components/common';
import IconMi from 'components/common/icon/IconMi';
// @ts-ignore
import * as color from 'react-color/lib/helpers/color';
import useBundleTranslation from 'i18n';

interface RichTextColorPickerTooltipProps {
    colorPickerAnchorEl: any;
    onSetColor: (value: string) => void;
    onClickAway: () => void;
    value: string;
    valueType: string;
}

const colorPickerDefaultPreset = [
    '#222222',
    '#4e4e4e',
    '#c9c9c9',
    '#FFFFFF',
    '#AE6ED3',
    '#7B439C',
    '#3A6D9D',
    '#5296D5',
    '#438F7A',
    '#60BF74',
    '#D8833B',
    '#EAC645',
    '#AC4132',
    '#CF4B38',
];

export default function RichTextColorPickerTooltip(props: RichTextColorPickerTooltipProps) {
    const { t } = useBundleTranslation(['components/common/rich-text']);
    const { colorPickerAnchorEl, onSetColor = () => {}, value, valueType, onClickAway = () => {} } = props;
    const [isChangedInput, setIsChangedInput] = useState<boolean>(false);
    const [previewColorHex, setPreviewColorHex] = useState<string>();
    const appTheme = useTheme();

    useEffect(() => {
        setIsChangedInput(false);
        setPreviewColorHex((value > '' ? value : colorPickerDefaultPreset[0]).toUpperCase());
    }, [value, colorPickerAnchorEl]);

    return (
        <Popper
            placement={'bottom-start'}
            open={!!colorPickerAnchorEl}
            anchorEl={colorPickerAnchorEl}
            container={() => {
                return colorPickerAnchorEl.parentElement;
            }}
            sx={{ zIndex: 1501 }}
            modifiers={[
                {
                    name: 'flip',
                    enabled: false,
                },
            ]}
        >
            <ClickAwayListener
                onClickAway={(event: Event | React.SyntheticEvent) => {
                    onClickAway();
                }}
            >
                <Box
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                    sx={{
                        mt: '5px',
                        width: '104px',
                        backgroundColor: 'background.default',
                        boxShadow: (theme) => `0px 4px 4px 0px ${alpha(theme.palette.text.primary, 0.16)}`,
                        padding: '4px',
                        borderRadius: '4px',
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: '2px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: previewColorHex,
                        }}
                    >
                        <Box
                            sx={{
                                color: color.getContrastingColor(previewColorHex),
                            }}
                        >
                            {valueType && !isChangedInput
                                ? t(`color_picker_tooltip.${valueType}_value`)
                                : previewColorHex}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: '4px',
                            py: 0.5,
                        }}
                    >
                        {colorPickerDefaultPreset.map((colorItem) => {
                            return (
                                <Box
                                    key={colorItem}
                                    sx={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: colorItem,
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        border: `1px solid ${darken(colorItem, 0.15)}`,
                                        '&:hover': {
                                            opacity: 0.75,
                                        },
                                    }}
                                    onClick={(event) => {
                                        onSetColor(colorItem);
                                    }}
                                />
                            );
                        })}
                        <Box
                            sx={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '2px',
                                display: 'inline-flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontSize: '16px',
                                '&:hover': {
                                    color: 'primary.main',
                                },
                            }}
                            onClick={() => {
                                onSetColor('');
                            }}
                        >
                            <IconMi icon={'clear-color'} />
                        </Box>
                    </Box>
                    <Stack direction={'row'}>
                        <Box
                            flexGrow={1}
                            sx={{
                                '& input:focus': {
                                    outline: 'none',
                                },
                            }}
                        >
                            <EditableInput
                                style={{
                                    label: {
                                        color: '#fff',
                                        fontSize: '12px',
                                        lineHeight: '18px',
                                    },
                                    input: {
                                        height: '24px',
                                        border: `1px solid ${alpha(appTheme.palette.text.primary, 0.4)}`,
                                        boxShadow: 'none',
                                        fontSize: '12px',
                                        padding: '0px 6px',
                                        color: appTheme.palette.primary.main,
                                        borderRadius: '3px',
                                        width: '100%',
                                    },
                                }}
                                value={previewColorHex}
                                onChange={(hexCode: any, e) => {
                                    if (color.isValidHex(hexCode) && hexCode.length === 7) {
                                        setIsChangedInput(true);
                                        setPreviewColorHex(hexCode.toUpperCase());
                                    }
                                }}
                            />
                        </Box>
                        <Box
                            flexShrink={0}
                            className={isChangedInput ? 'active' : undefined}
                            onClick={() => {
                                if (isChangedInput && previewColorHex) {
                                    onSetColor(previewColorHex);
                                }
                            }}
                            sx={{
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                ml: 0.25,
                                fontSize: '16px',
                                borderRadius: '3px',
                                color: (theme) => alpha(theme.palette.text.primary, 0.4),
                                '&.active': {
                                    color: (theme) => theme.palette.primary.main,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                    },
                                },
                            }}
                        >
                            <IconMi icon={'save'} />
                        </Box>
                    </Stack>
                </Box>
            </ClickAwayListener>
        </Popper>
    );
}
